import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../services/api";
import {
  FaStar,
  FaHeart,
  FaBookmark,
  FaCalendarAlt,
  FaClock,
  FaSpinner,
  FaArrowLeft,
  FaPlay,
  FaCheck,
  FaExclamationTriangle
} from "react-icons/fa";

function MovieDetails() {
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState({ rating: "", comment: "" });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [actionLoading, setActionLoading] = useState({ favorites: false, watchlist: false, review: false });

  useEffect(() => {
    const fetchMovie = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/movies/${id}`);
        setMovie(res.data);
      } catch (err) {
        console.error("Failed to fetch movie details");
        setMessage("Failed to load movie details");
        setMessageType("error");
      } finally {
        setLoading(false);
      }
    };

    fetchMovie();
  }, [id]);

  const addToFavorites = async () => {
    setActionLoading(prev => ({ ...prev, favorites: true }));
    try {
      await api.post("/user/favorites", {
        movieId: movie.id,
        title: movie.title,
        posterPath: movie.poster_path,
      });
      setMessage("Added to favorites! 🎉");
      setMessageType("success");
    } catch (err) {
      console.error(err.response?.data || err.message);
      setMessage("Already in favorites or error occurred.");
      setMessageType("error");
    } finally {
      setActionLoading(prev => ({ ...prev, favorites: false }));
    }
  };

  const addToWatchlist = async () => {
    setActionLoading(prev => ({ ...prev, watchlist: true }));
    try {
      await api.post("/user/watchlist", {
        movieId: movie.id,
        title: movie.title,
        posterPath: movie.poster_path,
      });
      setMessage("Added to watchlist! ✅");
      setMessageType("success");
    } catch (err) {
      console.error(err.response?.data || err.message);
      setMessage("Already in watchlist or error occurred.");
      setMessageType("error");
    } finally {
      setActionLoading(prev => ({ ...prev, watchlist: false }));
    }
  };

  const postReview = async e => {
    e.preventDefault();
    setActionLoading(prev => ({ ...prev, review: true }));
    try {
      await api.post("/reviews", {
        movieId: movie.id,
        rating: review.rating,
        comment: review.comment,
      });
      setMessage("Review submitted successfully! ✍️");
      setMessageType("success");
      setReview({ rating: "", comment: "" });
    } catch (err) {
      setMessage("Error posting review. Please try again.");
      setMessageType("error");
    } finally {
      setActionLoading(prev => ({ ...prev, review: false }));
    }
  };

  const formatRuntime = (minutes) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-4xl text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading movie details...</p>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Movie Not Found</h2>
          <p className="text-gray-600 mb-4">The movie you're looking for doesn't exist.</p>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <FaArrowLeft />
            <span>Back to Discover</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors mb-6"
        >
          <FaArrowLeft />
          <span>Back to Discover</span>
        </Link>
      </div>

      {/* Movie Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="md:flex">
            {/* Movie Poster */}
            <div className="md:w-1/3">
              {movie.poster_path ? (
                <img
                  src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                  alt={movie.title}
                  className="w-full h-96 md:h-full object-cover"
                />
              ) : (
                <div className="w-full h-96 md:h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <FaPlay className="text-6xl mx-auto mb-4" />
                    <span className="text-lg">No Image Available</span>
                  </div>
                </div>
              )}
            </div>

            {/* Movie Info */}
            <div className="md:w-2/3 p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">{movie.title}</h1>
                  {movie.tagline && (
                    <p className="text-xl text-gray-600 italic mb-4">"{movie.tagline}"</p>
                  )}
                </div>

                {/* Rating */}
                {movie.vote_average > 0 && (
                  <div className="flex items-center space-x-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full">
                    <FaStar className="text-yellow-500" />
                    <span className="font-bold text-lg">{movie.vote_average.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Movie Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center space-x-3 text-gray-600">
                  <FaCalendarAlt className="text-purple-600" />
                  <span>{formatDate(movie.release_date)}</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <FaClock className="text-purple-600" />
                  <span>{formatRuntime(movie.runtime)}</span>
                </div>
              </div>

              {/* Genres */}
              {movie.genres && movie.genres.length > 0 && (
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {movie.genres.map(genre => (
                      <span
                        key={genre.id}
                        className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {genre.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Overview */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Overview</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {movie.overview || "No overview available."}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={addToFavorites}
                  disabled={actionLoading.favorites}
                  className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading.favorites ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaHeart />
                  )}
                  <span>Add to Favorites</span>
                </button>

                <button
                  onClick={addToWatchlist}
                  disabled={actionLoading.watchlist}
                  className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading.watchlist ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaBookmark />
                  )}
                  <span>Add to Watchlist</span>
                </button>
              </div>

              {/* Status Message */}
              {message && (
                <div className={`mt-4 p-4 rounded-lg flex items-center space-x-2 ${messageType === "success"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                  }`}>
                  {messageType === "success" ? <FaCheck /> : <FaExclamationTriangle />}
                  <span>{message}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Review Section */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Write a Review</h2>
          <form onSubmit={postReview} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating (0-10)
                </label>
                <input
                  type="number"
                  name="rating"
                  value={review.rating}
                  onChange={e => setReview({ ...review, rating: e.target.value })}
                  placeholder="Enter rating"
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200"
                  min="0"
                  max="10"
                  step="0.1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review
                </label>
                <textarea
                  name="comment"
                  value={review.comment}
                  onChange={e => setReview({ ...review, comment: e.target.value })}
                  placeholder="Share your thoughts about this movie..."
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 h-24 resize-none"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={actionLoading.review}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
            >
              {actionLoading.review ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <FaStar />
                  <span>Submit Review</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MovieDetails;